import { motion } from "framer-motion";
import { MaskedLines } from "@/components/Reveal";
import { ArrowUpRight, Download, MapPin } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

/** Static, cinematic globe with animated gold arcs + city pills */
function GlobeVisual() {
    return (
        <div className="relative w-full aspect-square max-w-[640px] mx-auto">
            {/* halo rings */}
            <div className="absolute inset-0 rounded-full pointer-events-none" style={{ border: "1px solid rgba(212,175,55,0.10)" }} />
            <div className="absolute inset-6 rounded-full pointer-events-none" style={{ border: "1px solid rgba(212,175,55,0.06)" }} />
            <div className="absolute inset-14 rounded-full pointer-events-none" style={{ border: "1px dashed rgba(212,175,55,0.10)" }} />

            {/* Earth image */}
            <motion.div
                className="absolute inset-[15%] rounded-full overflow-hidden"
                style={{ boxShadow: "0 30px 100px -30px rgba(212,175,55,0.35), inset 0 0 60px rgba(11,19,32,0.6)" }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.6, ease: [0.25, 0.1, 0.25, 1] }}
            >
                <img
                    src="https://images.unsplash.com/photo-1614730321146-b6fa6a46bcb4?w=1200&auto=format&fit=crop&q=80"
                    alt="Earth from space at night"
                    className="w-full h-full object-cover"
                    style={{ filter: "brightness(0.85) contrast(1.05) saturate(1.1)" }}
                />
                {/* warm gold glow overlay */}
                <div className="absolute inset-0 pointer-events-none"
                    style={{
                        background: "radial-gradient(ellipse at 40% 60%, rgba(212,175,55,0.18) 0%, transparent 55%), radial-gradient(circle at 70% 40%, rgba(232,220,196,0.08) 0%, transparent 50%)",
                    }}
                />
                {/* rotating shimmer */}
                <motion.div
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: "conic-gradient(from 0deg, transparent, rgba(212,175,55,0.06), transparent 40%)" }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                />
            </motion.div>

            {/* Gold flight arcs (SVG overlay) */}
            <svg
                viewBox="0 0 400 400"
                className="absolute inset-0 w-full h-full pointer-events-none"
                preserveAspectRatio="xMidYMid meet"
                style={{ zIndex: 20 }}
            >
                <defs>
                    <linearGradient id="arc-gold-2" x1="0%" y1="100%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.6" />
                        <stop offset="55%" stopColor="#F0DFA1" stopOpacity="1" />
                        <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.5" />
                    </linearGradient>
                </defs>

                {/* Arcs */}
                <path d="M 90 190 Q 200 90 320 210" fill="none" stroke="url(#arc-gold-2)" strokeWidth="2" strokeLinecap="round" />
                <path d="M 70 250 Q 200 170 330 270" fill="none" stroke="url(#arc-gold-2)" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M 110 310 Q 220 220 300 320" fill="none" stroke="url(#arc-gold-2)" strokeWidth="1.5" strokeLinecap="round" opacity="0.9" />
                <path d="M 80 220 Q 240 260 330 220" fill="none" stroke="url(#arc-gold-2)" strokeWidth="1.7" strokeLinecap="round" opacity="0.85" />
                <path d="M 140 150 Q 260 120 310 250" fill="none" stroke="url(#arc-gold-2)" strokeWidth="1.5" strokeLinecap="round" opacity="0.75" />
                <path d="M 80 210 Q 200 130 320 210" fill="none" stroke="#F0DFA1" strokeWidth="1" strokeDasharray="3 6" strokeLinecap="round" opacity="0.8" />

                {/* Airplanes */}
                <g fill="#F0DFA1">
                    <path transform="translate(200 155) rotate(-20)" d="M 0 -5 L 5 4 L 0 1 L -5 4 Z" />
                    <path transform="translate(270 220) rotate(30)" d="M 0 -5 L 5 4 L 0 1 L -5 4 Z" />
                    <path transform="translate(150 260) rotate(50)" d="M 0 -5 L 5 4 L 0 1 L -5 4 Z" />
                </g>

                {/* Gold dots */}
                <g fill="#D4AF37">
                    <circle cx="90" cy="190" r="4" />
                    <circle cx="320" cy="210" r="4" />
                    <circle cx="70" cy="250" r="4" />
                    <circle cx="330" cy="270" r="4" />
                    <circle cx="110" cy="310" r="4" />
                    <circle cx="300" cy="320" r="4" />
                </g>
            </svg>

            {/* Floating city pills — on top of everything */}
            <div className="absolute inset-0" style={{ zIndex: 30, pointerEvents: "none" }}>
                {[
                    { city: "Tokyo", top: "16%", left: "56%" },
                    { city: "NYC", top: "32%", left: "74%" },
                    { city: "Lisbon", top: "46%", left: "4%" },
                    { city: "Bali", top: "70%", left: "56%" },
                ].map((p) => (
                    <div
                        key={p.city}
                        style={{
                            position: "absolute",
                            top: p.top,
                            left: p.left,
                            background: "rgba(11,19,32,0.85)",
                            backdropFilter: "blur(12px)",
                            WebkitBackdropFilter: "blur(12px)",
                            border: "1px solid rgba(212,175,55,0.2)",
                            borderRadius: 999,
                            padding: "6px 14px",
                            fontSize: 12,
                            color: "rgba(255,255,255,0.9)",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            whiteSpace: "nowrap",
                        }}
                    >
                        <span style={{ width: 6, height: 6, borderRadius: 999, background: "#D4AF37", display: "inline-block" }} />
                        {p.city}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function Hero() {
    const { t } = useTranslation();
    return (
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden" data-testid="hero-section">
            {/* soft gold glow behind globe */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(212,175,55,0.10) 0%, transparent 70%)", filter: "blur(40px)" }} />

            <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-[1.05fr_1fr] gap-16 items-center">
                <div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        className="overline mb-6 flex items-center gap-3"
                    >
                        <span className="inline-block w-8 h-px" style={{ background: "var(--gold)" }} />
                        {t("hero.overline")}
                    </motion.div>

                    <h1 className="font-serif text-white tracking-tighter" style={{ fontSize: "clamp(3rem, 7vw, 6rem)", lineHeight: 0.98 }}>
                        <MaskedLines
                            lines={[t("hero.line1"), <span key="i" style={{ fontStyle: "italic", color: "var(--gold-pale)" }}>{t("hero.line2")}</span>]}
                            delay={0.3}
                        />
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.4, duration: 0.9 }}
                        className="mt-8 text-lg md:text-xl text-white/70 max-w-xl leading-relaxed"
                    >
                        {t("hero.description")}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.6, duration: 0.9 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a href="#download" className="btn-gold" data-testid="hero-download-app">
                            <Download size={18} weight="regular" />
                            {t("hero.buttons.download")}
                        </a>
                        <a href="/auth?mode=signup" className="btn-ghost" data-testid="hero-get-started">
                            {t("hero.buttons.getStarted")}
                            <ArrowUpRight size={16} />
                        </a>
                        <a href="#atlas" className="btn-ghost" data-testid="hero-explore-map">
                            <MapPin size={16} />
                            {t("hero.buttons.exploreMap")}
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 2, duration: 1 }}
                        className="mt-16 grid grid-cols-3 gap-6 max-w-lg"
                        data-testid="hero-stats"
                    >
                        {[
                            { n: "2.4M+", l: t("hero.stats.travelers") },
                            { n: "195", l: t("hero.stats.countries") },
                            { n: "18M", l: t("hero.stats.stamps") },
                        ].map((s, i) => (
                            <div key={i} className="border-l pl-4" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
                                <div className="font-serif text-3xl md:text-4xl text-white">{s.n}</div>
                                <div className="text-[11px] tracking-[0.2em] uppercase text-white/50 mt-1">{s.l}</div>
                            </div>
                        ))}
                    </motion.div>
                </div>

                <div className="relative">
                    <GlobeVisual />
                </div>
            </div>
        </section>
    );
}
